// CareLogix™ ORACLE — React hook exposing the unified member session.
//
// Phase 39 — the homepage and any other ORACLE consumer should call this
// hook instead of poking localStorage directly. It hydrates synchronously
// from whatever the auth flow stored, then revalidates against /api/auth/me
// (and falls back to /api/membership/me) so the UI never flashes a stale
// guest state for a signed-in Enterprise member.
import { useEffect, useState } from "react";
import {
  fetchOracleMemberMe,
  getStoredOracleMember,
  normalizeOracleMember,
} from "@/auth/oracleMemberSession";

export function useOracleMemberSession() {
  const [loading, setLoading] = useState(true);
  const [member, setMember] = useState(null);

  useEffect(() => {
    let mounted = true;

    async function load() {
      const stored = normalizeOracleMember(getStoredOracleMember());
      if (stored && mounted) setMember(stored);

      const fresh = await fetchOracleMemberMe();
      if (!mounted) return;
      setMember(fresh || stored || null);
      setLoading(false);
    }

    load();

    // Refetch whenever the auth client broadcasts a session change so the
    // homepage reacts immediately after sign-in / sign-out.
    function onAuthChanged() {
      load();
    }
    try {
      window.addEventListener("carelogix:auth-changed", onAuthChanged);
    } catch {
      /* ignore */
    }
    return () => {
      mounted = false;
      try {
        window.removeEventListener("carelogix:auth-changed", onAuthChanged);
      } catch {
        /* ignore */
      }
    };
  }, []);

  const isAuthenticated = Boolean(
    member && member.id && !member.guest && member.tier && member.tier !== "guest",
  );
  const isGuest = Boolean(member && (member.guest || member.tier === "guest"));

  return {
    loading,
    member,
    isAuthenticated,
    isGuest,
  };
}

export default useOracleMemberSession;
